import { createRouter, createWebHistory } from "vue-router";
import { computed } from "vue"
import store from "@/store/store";
import {Roles} from '@/models/Roles';
import PermissionService from '@/utils/PermissionService';
import useI18nGlobal from "@/utils/i18n";
import useUserLanguage from "@/utils/user/useUserLanguage";
import { PUBLIC_ROUTES } from '@/modules/Login/constants/login.constant';
import { loginRoute } from '@/modules/Login/services/returnAddress';

export const routes = [
  {
    path: '/',
    name: 'root',
    beforeEnter: () => {
      const isAuthenticated = computed(() => store.getters["auth/isAuthenticated"]);
      const currentClientName = computed(() => store.getters["user/getClientName"]);
      const clientRoles = computed(() => store.getters["user/getUserRoles"]);
      
      if (isAuthenticated.value && currentClientName.value){
        Object.getOwnPropertyNames(clientRoles.value).length > 1
          ? router.push({ name: "clients-overview"})
          : router.push({name: 'map-overview', params: {clientName: currentClientName.value}})
      } else if(isAuthenticated.value && !currentClientName.value){
        router.push({ name: "clients-overview"})
      } else {
        router.push({name: 'login'})
      }
    }
  },
  {
    path: '/editor/:mapId',
    name: 'editor',
    props: true,
    meta: {permission: Roles.Student},
    // route level code-splitting
    component: () => import("@/pages/Editor.vue")
  },
  {
    path: '/login',
    name: 'login',
    component: () => import("@/pages/Login.vue")
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import("@/pages/ResetPassword.vue")
  },
  {
    path: '/register',
    name: 'register',
    component: () => import("@/pages/RegisterUser.vue")
  },
  {
    path: '/decline',
    name: 'decline',
    component: () => import("@/pages/DeclineInvitation.vue")
  },
  {
    path: '/clients',
    // meta: {permission: Roles.SuperUser},
    name: 'clients-overview',
    component: () => import("@/pages/ClientsOverview.vue")
  },
  {
    path: '/packages',
    name: 'packages-overview',
    // Guarded per route rather than through `meta.permission`: the global
    // `beforeEach` only evaluates that on the cold-start branch, so an
    // in-session navigation would walk straight past it. A `beforeEnter`
    // runs on every navigation into the route. The API is superuser-only
    // regardless; this keeps the section out of the UI for everyone else.
    beforeEnter: () => {
      const userRole = store.getters["user/getCurrentUserRole"];
      if (userRole !== Roles.Superuser) return {name: 'clients-overview'};
      return true;
    },
    component: () => import("@/pages/PackagesOverview.vue")
  },
  {
    path: '/fonts',
    name: 'fonts-overview',
    // Guarded per route, for the same reason as the packages overview.
    beforeEnter: () => {
      const userRole = store.getters["user/getCurrentUserRole"];
      if (userRole !== Roles.Superuser) return {name: 'clients-overview'};
      return true;
    },
    component: () => import("@/pages/FontsOverview.vue")
  },
  {
    path: '/clients/:clientName',
    name: 'map-overview',
    props: true,
    component: () => import("@/pages/MapOverview.vue")
  },
  { path: "/:pathMatch(.*)*", redirect: "login" }
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  linkActiveClass: "active",
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  }
});

router.beforeEach(async (to, from, next) => {
  const permission = to.meta?.permission || null;
  const isAuthenticated = computed(() => store.getters["auth/isAuthenticated"]);
  const userRole = computed(() => store.getters["user/getCurrentUserRole"]);
  const clientName = computed(() => store.getters["user/getClientName"]);
  
  // On a cold start, ask the server whether the browser already has a
  // session the Map Builder may use -- its own, or one opened by the Map
  // Viewer on the same host.
  if (!isAuthenticated.value && !PUBLIC_ROUTES.includes(to.name)) {
    try{
      const details = await store.dispatch("user/fetchSessionUser");
      await store.dispatch("auth/setAuthentication", true);

      // get language settings
      const {getLanguageSetByCode} = useUserLanguage();

      if(!details.language) {
        const {getCurrentLang} = useI18nGlobal();
        await store.dispatch("user/userLanguageSet", getLanguageSetByCode(getCurrentLang()));
      }
      if(details.language) await store.dispatch("user/userLanguageSet", getLanguageSetByCode(details.language));

      // define user's roles if it's not
      if(!userRole.value){
        const clientRoles = details.client_roles;
        await store.dispatch("user/parseUserRoles", clientRoles);
        await store.dispatch("user/saveClientName", Object.getOwnPropertyNames(clientRoles)[0]);
        await store.dispatch("user/defineUserRole", clientName.value);
      }

      // check user's permission if page required it
      if(permission){
        const hasUserPermission = computed(() => PermissionService.permissionCheck({userRole: userRole.value, permission}))
        if(!hasUserPermission.value) {
          next({name: 'clients-overview'});
        } else {
          next();
        }
      } else next();
    } catch(err){
      await store.dispatch("auth/setAuthentication", false);
      // Remember what was asked for, so signing in leads back to it.
      next(loginRoute(to.fullPath));
    }
  } else {
    next()
  }
  
  // if not logged in && location to is not login or restore-password
  /*if (!isAuthenticated.value && permission) {
    if (PermissionService.permissionCheck({userRole, permission})) {
      console.log('Permission check');
      next();
    } else {
      console.log('Permission failed');
      next({ name: "login" });
    }
  }*/ /*else {
    next();
  }*/
});

export default {router, routes};
